import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { Actions as EMPLOYEEACTIONS } from "../../store/actions/employeeActions";
import Select from "react-select";
import AuditLogRow from "./AuditLogRow";
import { auditTypes } from "./AuditTypes";
import { orderBy, set } from "lodash";
import HeaderSort from "@/common/tables/HeaderSort";
import Pagination from "@/common/tables/Pagination";
import DatePicker from "react-datepicker";
import { DateInputFormat } from "@/common/DateComponents";
import { isAfter, isBefore, subMonths } from "date-fns";
import NoItemsRow from "@/common/NoItemsRow";

const titles = [
	{ title: "Name", code: "userName" },
	{ title: "Action", code: "action" },
	{ title: "Type", code: "type" },
	{ title: "Date", code: "date" },
];
const actionFilterOptions = [
	{ value: "edit", label: "Edit" },
	{ value: "create", label: "Create" },
	{ value: "delete", label: "Delete" },
	{ value: "assign", label: "Assign" },
	{ value: "unassign", label: "Unassign" },
	{ value: "invite", label: "Invite" },
];

const AuditLogs = ({ auditLogs, nextToken, getEmployeeAuditLogs, typeFilterOptions, agreementId }) => {
	const [typeFilter, setTypeFilter] = useState();
	const [actionFilter, setActionFilter] = useState();
	const [from, setFrom] = useState(null);
	const [to, setTo] = useState(new Date());

	const [sortType, setSortType] = useState("desc");
	const [sortBy, setSortBy] = useState("date");

	const itemsPerPage = 12;
	const [page, setPage] = useState(0);
	const start = page * itemsPerPage;
	const end = (page + 1) * itemsPerPage;

	const filteredLogs = useMemo(() => {
		const filters = auditLogs?.filter((log) => {
			const typeFilterMatch = typeFilter ? log.type === typeFilter : true;
			const actionFilterMatch = actionFilter ? log.action === actionFilter.value : true;
			const dateFilterMatch =
				(isAfter(new Date(log.date), from) && isBefore(new Date(log.date), to)) || !from || !to;

			return typeFilterMatch && actionFilterMatch && dateFilterMatch;
		});

		return orderBy(filters, [sortBy], [sortType]);
	}, [auditLogs, sortType, actionFilter, typeFilter]);

	useEffect(() => {
		getEmployeeAuditLogs({ agreementId, nextToken, from, to });
	}, [from, to]);

	return (
		<React.Fragment>
			<div className="content">
				<div
					className="col-xs-12 p-0 mb-5"
					style={{ display: "flex", gap: "15px" }}
				>
					<div className="col-xs-2 p-0">
						<label>Action</label>
						<Select
							options={actionFilterOptions}
							value={actionFilter}
							onChange={(e) => {
								setActionFilter(e);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-2 p-0">
						<label>Type</label>
						<Select
							options={typeFilterOptions}
							value={typeFilterOptions?.find((t) => t.value === typeFilter)}
							onChange={(e) => {
								setTypeFilter(e?.value);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-2 p-0">
						<label>From</label>
						<DatePicker
							selected={from}
							customInput={<DateInputFormat />}
							onChange={(date) => setFrom(date)}
							maxDate={to}
							dateFormat={"do MMM yyyy"}
							wrapperClassName="full-width"
							placeholderText="Select..."
						/>
					</div>
					<div className="col-xs-2 p-0">
						<label>To</label>
						<DatePicker
							selected={to}
							customInput={<DateInputFormat />}
							onChange={(date) => setTo(date)}
							maxDate={new Date()}
							minDate={from}
							dateFormat="do MMM yyyy"
							wrapperClassName="full-width"
							placeholderText="Select..."
						/>
					</div>
				</div>
				<table
					id="table-timesheets"
					className="js-table-sections table table-hover"
					style={{ display: `${filteredLogs?.length === 0 ? "" : "none"}` }}
				>
					<NoItemsRow name="Audit Logs" />
				</table>
				<div
					className="table-responsive"
					style={{ overflow: "auto", display: `${filteredLogs?.length > 0 ? "" : "none"}` }}
				>
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							borderBottom: "1px #b5b5b5 solid",
						}}
					>
						<Pagination
							itemsPerPage={itemsPerPage}
							items={filteredLogs}
							page={page}
							setPage={setPage}
						/>
					</div>

					<div className="dataTables_wrapper no-footer">
						<table className="js-table-sections table table-hover">
							<thead style={{ display: "table-header-group" }}>
								<tr>
									{titles.map((t) => (
										<HeaderSort
											title={t.title}
											code={t.code}
											sortBy={sortBy}
											setSortBy={setSortBy}
											sortType={sortType}
											setSortType={setSortType}
										/>
									))}
									<th style={{ userSelect: "none" }}>EFFECTIVE DATE</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{filteredLogs ? (
									filteredLogs?.slice(start, end)?.map((logEntry) => (
										<AuditLogRow
											key={logEntry.id}
											logEntry={logEntry}
										/>
									))
								) : (
									<tr>
										<td colSpan="6"> Loading Audit Logs, hold on...</td>
									</tr>
								)}
								{nextToken && (
									<tr>
										<td colSpan="6">
											<span onClick={() => getEmployeeAuditLogs({ agreementId })}>
												View More Logs
											</span>
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

const mapStateToProps = (state) => {
	//Get all unique types of audit logs
	const typeFilterOptions = Object.entries(auditTypes).map(([key, value]) => ({
		value: key,
		label: value.name,
	}));

	const sortedTypeFilterOptions = orderBy(typeFilterOptions, ["label"], ["asc"]);

	return {
		typeFilterOptions: sortedTypeFilterOptions,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getEmployeeAuditLogs: ({ agreementId, nextToken, from, to }) =>
		dispatch(EMPLOYEEACTIONS.getEmployeeAuditLogs({ agreementId, nextToken, from, to })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogs);
